import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import DescriptionIcon from "@mui/icons-material/Description";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getLocalValue,
  LoacalVariables,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import http from "../../../commonFunctions/httpClient";
import { useVendorOrdersHub } from "../../../commonFunctions/vendorOrdersHub";
import { ingestOrders, isPharmacyStatusClosed, useNewOrderHighlights } from "../../../commonFunctions/newOrderHighlights";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import {
  formatAmount,
  formatDate,
  getStatusStyle,
  AcceptOrderButton,
  NewOrderTag,
  newOrderBlinkSx,
  ORDER_COLORS as COLORS,
  orderFieldSx as fieldSx,
  orderFilterChipSx as filterChipSx,
  StatusChip,
  toDateKey,
} from "../../../Components/Orders/OrderCardParts";
import { UpdateStatus } from "./updateStatus";
import WSDataGridNew, { IColumns } from "../../../Components/DataGrid/WSDataGridNew";

type OrderType = "product" | "prescription";

// Product and prescription orders come from different APIs; both are mapped to this shape
interface PharmacyOrderItem {
  id: string;
  displayId: string;
  type: OrderType;
  customerName: string;
  itemCount: number;
  totalAmount: number;
  payment: string;
  orderDate: string;
  status: string;
}

const PAGE_SIZE = 12;

const TYPE_LABELS: Record<OrderType, string> = {
  product: "Product order",
  prescription: "Prescription order",
};

// Finished orders move from Orders to Order History
const isClosedOrder = (order: PharmacyOrderItem) => isPharmacyStatusClosed(order.status);
const isDeliveredOrder = (order: PharmacyOrderItem) => (order.status || "").trim().toLowerCase() === "delivered";

// Product orders move through these steps one at a time; "value" is what the API stores
const STATUS_CHAIN = [
  { key: "pending", value: "Pending", label: "Pending" },
  { key: "packed", value: "packed", label: "Packed" },
  { key: "shipped", value: "Shipped", label: "Shipped" },
  { key: "delivered", value: "Delivered", label: "Delivered" },
];

// The step after the order's current status, or null if it isn't in the chain / is the last step
const getNextStatus = (status: string) => {
  const index = STATUS_CHAIN.findIndex((step) => step.key === (status || "").trim().toLowerCase());
  return index === -1 ? null : STATUS_CHAIN[index + 1] || null;
};

const mapProductOrder = (order: any): PharmacyOrderItem => ({
  id: order.orderId,
  // Order number if the API sends one, otherwise a short code from the order id (never the full id)
  displayId: order.orderNumber ? String(order.orderNumber) : `PO${String(order.orderId || "").slice(-6).toUpperCase()}`,
  type: "product",
  customerName: order.userName || "Unknown",
  itemCount: Number(order.itemCount) || 0,
  totalAmount: Number(order.totalAmount) || 0,
  payment: order.paymentMethod || "",
  orderDate: order.orderDate || "",
  status: order.status || "Pending",
});

const mapPrescriptionOrder = (order: any): PharmacyOrderItem => {
  const id = order.id || order.orderId || "";
  return {
    id,
    displayId: order.orderNumber ? String(order.orderNumber) : `HC${String(order.orderId || id).slice(-6).toUpperCase()}`,
    type: "prescription",
    customerName: order.patient?.username || order.userName || order.patientName || "N/A",
    itemCount: Number(order.orderInfo?.totalItems ?? order.itemCount ?? order.itemsCount) || 0,
    totalAmount:
      Number(order.orderInfo?.subtotal || order.paymentInfo?.orderPrice || order.totalAmount || order.price) || 0,
    payment: order.paymentInfo?.paymentStatus || order.paymentMethod || order.paymentStatus || "Unpaid",
    orderDate: order.timeline?.orderPlaced || order.orderDate || "",
    status: order.timeline?.status || order.status || "Pending",
  };
};

interface OrderCardProps {
  order: PharmacyOrderItem;
  isNew: boolean;
  onAccept: () => void;
  onView: () => void;
  onUpdateStatus: () => void;
  onAdvance: (next: { value: string; label: string }) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, isNew, onAccept, onView, onUpdateStatus, onAdvance }) => {
  const isPrescription = order.type === "prescription";
  // Status changes are only supported for product orders (prescription orders go through billing)
  const canUpdateStatus = !isPrescription && !isClosedOrder(order);
  const statusStyle = getStatusStyle(order.status);
  const nextStatus = getNextStatus(order.status);

  return (
    <Paper
      elevation={0}
      onClick={onView}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: `1px solid ${COLORS.border}`,
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": { boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)", borderColor: COLORS.primary },
        ...(isNew ? newOrderBlinkSx : {}),
      }}
    >
      {/* Header: order id, date, status */}
      <Box sx={{ px: 2, py: 1.5, bgcolor: COLORS.light, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "0.95rem" }} noWrap>
            #{order.displayId}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>
            {formatDate(order.orderDate)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {isNew && <NewOrderTag />}
          <StatusChip label={order.status} />
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {/* Customer */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: COLORS.primary }} />
          <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }} noWrap>
            {order.customerName}
          </Typography>
        </Box>

        {/* Order type + item count */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Chip
            size="small"
            icon={isPrescription ? <DescriptionIcon sx={{ fontSize: 16 }} /> : <LocalPharmacyIcon sx={{ fontSize: 16 }} />}
            label={TYPE_LABELS[order.type]}
            variant="outlined"
            sx={{ borderColor: COLORS.border, color: COLORS.dark, "& .MuiChip-icon": { color: COLORS.primary } }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Inventory2Icon sx={{ fontSize: 16, color: COLORS.muted }} />
            <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem" }}>
              {order.itemCount} {order.itemCount === 1 ? "item" : "items"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer: total, payment, action */}
      <Divider sx={{ borderStyle: "dashed", borderColor: COLORS.border, mx: 2 }} />
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "1rem" }}>
            {formatAmount(order.totalAmount)}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>
            {order.payment || "-"}
          </Typography>
        </Box>
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          sx={{ color: COLORS.primary, fontWeight: 600, textTransform: "none" }}
        >
          View details
        </Button>
      </Box>
      {isNew ? (
        // A new order is accepted first; then the next-step button below takes its place
        <Box sx={{ px: 2, pb: 2 }}>
          <AcceptOrderButton onAccept={onAccept} />
        </Box>
      ) : canUpdateStatus ? (
        <Box sx={{ px: 2, pb: 2 }}>
          {nextStatus ? (
            // Next step in the chain: Pending -> Packed -> Shipped -> Delivered
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={(e) => {
                e.stopPropagation();
                onAdvance(nextStatus);
              }}
              sx={{
                bgcolor: COLORS.primary,
                color: "#fff",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#077352" },
              }}
            >
              Mark as {nextStatus.label}
            </Button>
          ) : (
            // Status outside the chain: show it and let the vendor pick a new one
            <Button
              fullWidth
              variant="outlined"
              aria-label={`Status: ${order.status}. Change status`}
              onClick={(e) => {
                e.stopPropagation();
                onUpdateStatus();
              }}
              sx={{
                borderColor: statusStyle.color,
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: statusStyle.bg, borderColor: statusStyle.color, filter: "brightness(0.97)" },
              }}
            >
              {order.status}
            </Button>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};

interface PharmacyOrderListProps {
  mode?: "active" | "history";
}

export const PharmacyOrderList: React.FC<PharmacyOrderListProps> = React.memo(({ mode = "active" }) => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const isHistory = mode === "history";

  // ?status=<status> comes from the dashboard cards (e.g. Pending Orders)
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get("status") || "").trim().toLowerCase();
  const clearStatusParam = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("status");
    setSearchParams(next, { replace: true });
  };

  const [vendorId, setVendorId] = useState<string>("");
  const [orders, setOrders] = useState<PharmacyOrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [partialError, setPartialError] = useState(false);
  const { isNew, accept } = useNewOrderHighlights();

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("All");
  // Status line on the Orders page. It lives in the page address (?status=shipped), so opening an
  // order and coming back, or using the browser Back button, keeps the same status selected.
  const statusFilter = isHistory ? "All" : statusParam || "All";
  const setStatusFilter = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "All") next.delete("status");
    else next.set("status", key);
    setSearchParams(next, { replace: true });
  };
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Update status dialog
  const [statusOrder, setStatusOrder] = useState<PharmacyOrderItem | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  // "Mark as <next step>" confirmation
  const [advance, setAdvance] = useState<{ order: PharmacyOrderItem; next: { value: string; label: string } } | null>(null);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setVendorId(userId || "");
    if (!userId) setLoading(false);
  }, []);

  useEffect(() => {
    if (isHistory) {
      updateMainHeader("Order History");
      breadCrumbsStore.updateBreadCrumbs([
        { Orders: "/pharmacy-orders/list" },
        { "Order History": "/pharmacy-orders/history" },
      ]);
    } else {
      updateMainHeader("Orders");
      breadCrumbsStore.updateBreadCrumbs([{ Orders: "/pharmacy-orders/list" }]);
    }
  }, [isHistory]);

  const fetchOrders = useCallback(async (showLoading = true) => {
    if (!vendorId) return;
    if (showLoading) setLoading(true);
    setError(false);
    setPartialError(false);

    // Load both order types together; one failing shouldn't hide the other
    const [productResult, prescriptionResult] = await Promise.allSettled([
      http.get(`pharmacy/ph_order_/ByVendor/${vendorId}`),
      http.get(`pharmacy/PrescriptionOrder/ByVendor/${vendorId}`),
    ]);

    const toList = (result: PromiseSettledResult<any>) =>
      result.status === "fulfilled" && Array.isArray(result.value?.data) ? result.value.data : [];

    const merged: PharmacyOrderItem[] = [
      ...toList(productResult).map(mapProductOrder),
      ...toList(prescriptionResult).map(mapPrescriptionOrder),
    ];
    // Latest orders first
    merged.sort((a, b) => (new Date(b.orderDate).getTime() || 0) - (new Date(a.orderDate).getTime() || 0));

    const failures = [productResult, prescriptionResult].filter((r) => r.status === "rejected");
    failures.forEach((r: any) => console.error("Failed to fetch pharmacy orders:", r.reason));

    if (failures.length === 2) {
      if (showLoading) {
        setError(true);
        setOrders([]);
      }
    } else {
      setPartialError(failures.length === 1);
      // Orders not seen before blink until opened (only trust the list if both requests worked)
      ingestOrders(
        merged.map((o) => ({ id: o.id, needsAttention: !isClosedOrder(o) })),
        Array.isArray((productResult as any).value?.data) && Array.isArray((prescriptionResult as any).value?.data)
      );
      setOrders(merged);
    }
    if (showLoading) setLoading(false);
  }, [vendorId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // New or updated orders pushed live by the backend
  useVendorOrdersHub(() => fetchOrders(false));

  const closeStatusDialog = () => {
    setStatusOrder(null);
    setSelectedStatus("");
  };

  // Returns true when the API accepted the change
  const submitStatusUpdate = async (orderId: string, newStatus: string) => {
    try {
      const response: any = await http.put(`pharmacy/Ph_Order_/update-status`, {
        orderId,
        status: newStatus,
        vendorId,
      });
      if (!response?.success) throw new Error(response?.message || "Status update failed");

      // Reflect the change immediately, then sync with the server
      setOrders((prev) => prev.map((o) => (o.id === orderId && o.type === "product" ? { ...o, status: newStatus } : o)));
      showMessage(`Order status updated to ${newStatus}`, "success");
      fetchOrders(false);
      return true;
    } catch (e: any) {
      console.error("Status update failed:", e);
      showMessage(e?.response?.data?.message || e?.message || "Failed to update status", "error");
      return false;
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusOrder || !selectedStatus || !vendorId) return;
    const orderId = statusOrder.id;
    const newStatus = selectedStatus;
    closeStatusDialog();
    await submitStatusUpdate(orderId, newStatus);
  };

  const handleConfirmAdvance = async () => {
    if (!advance || !vendorId) return;
    setAdvancing(true);
    await submitStatusUpdate(advance.order.id, advance.next.value);
    setAdvancing(false);
    setAdvance(null);
  };

  const viewOrder = (order: PharmacyOrderItem) =>
    navigate(order.type === "prescription" ? `/prescription-order-details/${order.id}` : `/pharmacy-order-details/${order.id}`);

  // Open orders on the Orders page, finished ones on Order History. The Orders page also receives
  // delivered orders, which it shows only under its Delivered filter.
  const scopedOrders = useMemo(
    () =>
      orders.filter((order) =>
        isHistory
          ? isClosedOrder(order) && (!statusParam || (order.status || "").trim().toLowerCase() === statusParam)
          : !isClosedOrder(order) || isDeliveredOrder(order)
      ),
    [orders, isHistory, statusParam]
  );

  // Payment options are built from the values actually present in the data
  const paymentOptions = useMemo(() => {
    const payments = Array.from(new Set(scopedOrders.map((o) => o.payment).filter(Boolean)));
    return ["All", ...payments];
  }, [scopedOrders]);

  // Status line: every step of the chain (pending -> packed -> shipped -> delivered), each with its
  // order count. Other statuses (such as OrderPlaced) are only listed under "All".
  const statusLine = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    scopedOrders.forEach((order) => {
      const key = (order.status || "").trim().toLowerCase();
      if (!key) return;
      const entry = counts.get(key);
      if (entry) entry.count += 1;
      else counts.set(key, { label: order.status.trim(), count: 1 });
    });
    return STATUS_CHAIN.map((step) => ({
      key: step.key,
      label: step.label,
      count: counts.get(step.key)?.count || 0,
    }));
  }, [scopedOrders]);

  // Orders still to deal with; delivered ones are not part of "All"
  const openOrderCount = scopedOrders.filter((order) => !isDeliveredOrder(order)).length;

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = scopedOrders.filter((order) => {
      if (!isHistory && statusFilter !== "All" && (order.status || "").trim().toLowerCase() !== statusFilter) return false;
      if (!isHistory && statusFilter === "All" && isDeliveredOrder(order)) return false;
      if (paymentFilter !== "All" && order.payment !== paymentFilter) return false;
      const dateKey = toDateKey(order.orderDate);
      if (fromDate && (!dateKey || dateKey < fromDate)) return false;
      if (toDate && (!dateKey || dateKey > toDate)) return false;
      if (term && !`${order.displayId} ${order.customerName}`.toLowerCase().includes(term)) return false;
      return true;
    });
    // Orders arrive newest first
    return sortOrder === "oldest" ? [...result].reverse() : result;
  }, [scopedOrders, isHistory, statusFilter, search, paymentFilter, fromDate, toDate, sortOrder]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, statusFilter, paymentFilter, fromDate, toDate, sortOrder]);

  const hasFilters =
    search || paymentFilter !== "All" || fromDate || toDate || (isHistory ? statusParam : statusFilter !== "All");

  const clearFilters = () => {
    if (statusParam) clearStatusParam();
    setSearch("");
    setPaymentFilter("All");
    setFromDate("");
    setToDate("");
  };

  // Table rows for Order History, in the chosen sort order
  const tableRows = useMemo(
    () =>
      filteredOrders.map((order) => ({
        id: `${order.type}-${order.id}`,
        orderNo: order.displayId,
        date: formatDate(order.orderDate),
        customer: order.customerName,
        type: TYPE_LABELS[order.type],
        items: order.itemCount,
        amount: formatAmount(order.totalAmount),
        payment: order.payment || "-",
        status: order.status,
        actions: "",
        raw: order,
      })),
    [filteredOrders]
  );

  const tableColumns: IColumns[] = [
    { header: "Order No", fieldName: "orderNo", type: "default" },
    { header: "Date", fieldName: "date", type: "default" },
    { header: "Customer", fieldName: "customer", type: "default" },
    { header: "Type", fieldName: "type", type: "default" },
    { header: "Items", fieldName: "items", type: "default" },
    { header: "Amount", fieldName: "amount", type: "default" },
    { header: "Payment", fieldName: "payment", type: "default" },
    { header: "Status", fieldName: "status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["View"],
      actionCallback: (action, row) => {
        if (action === "View") viewOrder(row.raw);
      },
    },
  ];

  // Clicking the selected step again goes back to All
  const selectStatus = (key: string) => {
    setStatusFilter(statusFilter === key ? "All" : key);
  };

  // Same width for every button so the gaps between them look equal; "All" uses the key "All"
  const renderStatusChip = (key: string, label: string, count: number) => (
    <Chip
      key={key}
      label={`${label} · ${count}`}
      aria-pressed={statusFilter === key}
      onClick={() => selectStatus(key)}
      sx={{ ...filterChipSx(statusFilter === key), flexShrink: 0, width: 150, height: 36 }}
    />
  );

  return (
    <Box sx={{ mt: 1 }}>
      {/* Filters only on Order History (which is a table); the Orders page shows cards only */}
      {isHistory && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: "16px" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by order ID or customer"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.muted }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || undefined }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || undefined }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Payment"
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                sx={fieldSx}
              >
                {paymentOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "All" ? "All payments" : option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Sort"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
                sx={fieldSx}
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Status line (Orders page): All, then each step of the order chain; click one to filter the cards.
          Every button is the same width and every gap is the same length. */}
      {!isHistory && !loading && !error && (
        <Box
          role="group"
          aria-label="Filter orders by status"
          sx={{ display: "flex", alignItems: "center", overflowX: "auto", mb: 2, pb: 0.5 }}
        >
          {[
            { key: "All", label: "All", count: openOrderCount },
            ...statusLine,
          ].map((item, index) => (
            <React.Fragment key={item.key}>
              {index > 0 && (
                // The same fixed-length line between every pair of buttons
                <Box aria-hidden="true" sx={{ flexShrink: 0, width: 32, height: 2, bgcolor: COLORS.border }} />
              )}
              {renderStatusChip(item.key, item.label, item.count)}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Result count */}
      {!loading && !error && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, px: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem" }}>
              {filteredOrders.length} {filteredOrders.length === 1 ? "order" : "orders"}
              {partialError && " · some orders couldn't be loaded, please refresh"}
            </Typography>
            {isHistory && statusParam && (
              <Chip
                size="small"
                label={`Status: ${statusParam.charAt(0).toUpperCase()}${statusParam.slice(1)}`}
                onDelete={clearStatusParam}
                sx={{ bgcolor: COLORS.light, color: COLORS.primary, fontWeight: 600 }}
              />
            )}
          </Box>
          {hasFilters && (
            <Button size="small" onClick={clearFilters} sx={{ color: COLORS.primary, textTransform: "none" }}>
              Clear filters
            </Button>
          )}
        </Box>
      )}

      {loading ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: COLORS.muted }}>Loading orders...</Typography>
        </Paper>
      ) : error ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: "#D32F2F" }}>Failed to load orders. Please try again.</Typography>
        </Paper>
      ) : filteredOrders.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", textAlign: "center" }}>
          <Typography sx={{ color: COLORS.dark, fontWeight: 600 }}>No orders found</Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem", mt: 0.5 }}>
            {hasFilters
              ? "Try changing or clearing the filters."
              : isHistory
                ? "Delivered, cancelled and refunded orders will appear here."
                : "New orders will appear here."}
          </Typography>
        </Paper>
      ) : isHistory ? (
        // Remount on filter change so the table goes back to page 1
        <WSDataGridNew
          key={`${search}|${paymentFilter}|${fromDate}|${toDate}|${sortOrder}|${statusParam}`}
          id="pharmacyOrderHistoryGrid"
          columns={tableColumns}
          data={tableRows}
          exportCsv={true}
          gridHeaderSx={{ backgroundColor: "whitesmoke", border: "1px solid whitesmoke" }}
        />
      ) : (
        <>
          <Grid container spacing={2}>
            {filteredOrders.slice(0, visibleCount).map((order) => (
              <Grid item xs={12} sm={6} lg={4} key={`${order.type}-${order.id}`}>
                <OrderCard
                  order={order}
                  isNew={isNew(order.id)}
                  onAccept={() => accept(order.id)}
                  onView={() => viewOrder(order)}
                  onUpdateStatus={() => setStatusOrder(order)}
                  onAdvance={(next) => setAdvance({ order, next })}
                />
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredOrders.length && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                sx={{ borderColor: COLORS.primary, color: COLORS.primary, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
              >
                Load more ({filteredOrders.length - visibleCount} remaining)
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={!!advance}
        onClose={advancing ? undefined : () => setAdvance(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Mark order as {advance?.next.label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {advance && `Order for ${advance.order.customerName} will change from ${advance.order.status} to ${advance.next.label}.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAdvance(null)} disabled={advancing} sx={{ color: COLORS.muted, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmAdvance}
            disabled={advancing}
            startIcon={advancing ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: COLORS.primary, borderRadius: "10px", textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#077352" } }}
          >
            {advancing ? "Updating..." : `Mark as ${advance?.next.label || ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      <UpdateStatus
        confirmOpen={!!statusOrder}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        handleCancelStatusChange={closeStatusDialog}
        handleConfirmStatusChange={handleConfirmStatusChange}
      />
    </Box>
  );
});

export const PharmacyOrderHistory: React.FC = () => <PharmacyOrderList mode="history" />;
